// Isochrone controller for managing isochrone calculations with Preact signals

import L from 'leaflet';
import { signal } from '@preact/signals';
import { LAYER_STYLES, DEFAULT_ISO_CONFIG } from '../constants';
import { IsochroneService, captureException, addBreadcrumb } from '../services';
import type { IsoConfig, IsoPoint } from '../types';

/**
 * Controller for isochrone calculations (logic only, no DOM manipulation)
 */
export class IsochroneController {
  private map: L.Map;
  private service: IsochroneService;
  private isochroneLayer: L.GeoJSON;

  // Signals for reactive state
  readonly config = signal<IsoConfig>({ ...DEFAULT_ISO_CONFIG });
  readonly lastPoint = signal<IsoPoint | null>(null);
  readonly loading = signal<boolean>(false);
  readonly hasResult = signal<boolean>(false);
  readonly resultLabel = signal<string>('');
  readonly hintText = signal<string>('Sélectionnez un marqueur');
  readonly filterMarkers = signal<boolean>(false);

  constructor(map: L.Map) {
    this.map = map;
    this.service = new IsochroneService();

    // Initialize isochrone layer
    this.isochroneLayer = L.geoJSON(undefined, {
      style: () => LAYER_STYLES.isochrone,
    }).addTo(this.map);
  }

  /**
   * Set point for isochrone calculation
   * @param point - Point data
   */
  setPoint(point: IsoPoint): void {
    this.lastPoint.value = point;
    this.hintText.value = `Prêt: ${point.name}`;
  }

  /**
   * Calculate isochrone for current point
   */
  async calculate(): Promise<void> {
    const point = this.lastPoint.value;
    if (!point || this.service.loading) return;

    this.loading.value = true;
    addBreadcrumb('Starting isochrone calculation', 'isochrone', 'info', {
      method: this.config.value.method,
      graph: this.config.value.graph,
      value: this.config.value.value,
    });

    try {
      const feature = await this.service.calculate(point, this.config.value);

      if (feature) {
        try {
          this.isochroneLayer.clearLayers();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('IsochroneController: clearLayers failed', err);
        }

        try {
          this.isochroneLayer.addData(feature);
        } catch (err) {
          captureException(err as Error, {
            component: 'IsochroneController',
            method: 'calculate',
            operation: 'addData',
          });
          // eslint-disable-next-line no-console
          console.error('IsochroneController: addData failed', err);
        }

        try {
          this.fitToIsochrone();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('IsochroneController: fitToIsochrone failed', err);
        }

        this.showResult();
        addBreadcrumb('Isochrone calculation successful', 'isochrone', 'info');
      }
    } catch (error) {
      captureException(error as Error, {
        component: 'IsochroneController',
        method: 'calculate',
        config: this.config.value,
        point,
      });
      this.showError();
    } finally {
      this.loading.value = false;
    }
  }

  /**
   * Clear isochrone layer
   */
  clear(): void {
    try {
      this.isochroneLayer.clearLayers();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('IsochroneController: clearLayers failed in clear()', err);
    }
    this.filterMarkers.value = false;
    this.hasResult.value = false;
    this.resultLabel.value = '';
    this.hintText.value = 'Sélectionnez un marqueur';
    this.lastPoint.value = null;
  }

  /**
   * Get the geometry of the current isochrone result
   * @returns GeoJSON geometry or null if no result
   */
  getIsochroneGeometry(): GeoJSON.Geometry | null {
    const layers = this.isochroneLayer.getLayers();
    if (layers.length === 0) return null;
    const layer = layers[0] as L.GeoJSON;
    const feature = (layer as unknown as { feature?: GeoJSON.Feature }).feature;
    return feature?.geometry ?? null;
  }

  /**
   * Get current configuration
   */
  getConfig(): IsoConfig {
    return { ...this.config.value };
  }

  private showResult(): void {
    const point = this.lastPoint.value;
    if (point) {
      const label = IsochroneService.getDisplayLabel(this.config.value, point.name);
      this.resultLabel.value = label;
      this.hasResult.value = true;
    }
  }

  private showError(): void {
    this.hintText.value = '❌ Erreur de calcul';
    this.hasResult.value = false;
  }

  private fitToIsochrone(): void {
    const bounds = this.isochroneLayer.getBounds();
    if (bounds.isValid()) {
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }
  }
}
